import {type ReactNode} from 'react';
import {Check, X} from 'lucide-react';
import {Badge, Button, Card} from '@kroll-resolver-crisp/ui-components-react';
import {
  BLUR_IMAGE_CLASS,
  SensitiveMediaControls,
  useSensitiveMedia
} from '@/components/shared/SensitiveMedia';
import {Thumbnail} from '@/components/shared/Thumbnail';
import {cn} from '@/lib/utils';

/** The "Latest …" caption above an example picker's tile grid. */
export function ExampleGridLabel({children}: {readonly children: ReactNode}) {
  return <p className="text-muted-foreground text-[10px] tracking-widest uppercase">{children}</p>;
}

/** Container for ExampleTiles: tiles are options in a multi-select listbox. */
export function ExampleTileGrid({
  label,
  children
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div
      role="listbox"
      aria-multiselectable="true"
      aria-label={label}
      className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {children}
    </div>
  );
}

type TileProps = {
  readonly title: string;
  readonly thumbnailUrl: string | null | undefined;
  readonly selected: boolean;
  readonly aspect: 'video' | 'square';
  readonly onToggle: () => void;
};

/** One pickable latest upload/pin. A Card so its edge, hover, focus and selected states (and, later, its corner radius) come from the design system. */
export function ExampleTile({title, thumbnailUrl, selected, aspect, onToggle}: TileProps) {
  const aspectClass = aspect === 'video' ? 'aspect-video' : 'aspect-square';
  const {thumbnailProps, controls} = useSensitiveMedia();
  return (
    // The toggle sits beside the option, not inside it: an option's children are presentational.
    <div className="group relative">
      <Card
        interactive
        size="sm"
        selected={selected}
        role="option"
        aria-selected={selected}
        aria-label={title}
        title={title}
        className="relative gap-0 p-0"
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}>
        {thumbnailUrl ? (
          <Thumbnail
            src={thumbnailUrl}
            alt=""
            className={cn(aspectClass, 'w-full object-cover', BLUR_IMAGE_CLASS)}
            {...thumbnailProps}
          />
        ) : (
          <div className={cn(aspectClass, 'bg-muted w-full')} />
        )}
        {selected ? (
          <span className="bg-primary text-primary-foreground absolute top-1 right-1 p-0.5">
            <Check className="size-3" />
          </span>
        ) : null}
        <span className="block truncate px-1.5 py-1 text-xs">{title}</span>
      </Card>
      {thumbnailUrl ? (
        <SensitiveMediaControls {...controls} compact className={cn(aspectClass, 'bottom-auto')} />
      ) : null}
    </div>
  );
}

type ChipProps = {
  readonly title: string;
  readonly thumbnailUrl: string | null | undefined;
  readonly thumbnailClassName: string;
  readonly onRemove: () => void;
};

/** A selected example, with its remove button. */
export function ExampleChip({title, thumbnailUrl, thumbnailClassName, onRemove}: ChipProps) {
  return (
    <li>
      <Badge variant="tag" className="h-auto max-w-full gap-2 p-1">
        {thumbnailUrl ? (
          <span className={cn('shrink-0 overflow-hidden', thumbnailClassName)}>
            <Thumbnail
              src={thumbnailUrl}
              alt=""
              className={cn('h-full w-full object-cover', BLUR_IMAGE_CLASS)}
            />
          </span>
        ) : null}
        <span className="max-w-56 truncate text-xs">{title}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={`Remove ${title}`}
          className="text-muted-foreground"
          onClick={onRemove}>
          <X />
        </Button>
      </Badge>
    </li>
  );
}
